import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  createMonitoringIcon,
  getMonitoringTypeFromToken,
  MONITORING_BADGE_STYLES,
  type MonitoringType,
} from "@/constants/monitoring";
import { useGetTeamActivityLogs } from "@/features/tokens/api/token.queries";
import { formatActivityTimestamp } from "@/lib/dateUtils";
import { ActivityLogTableSkeleton } from "@/components/Common/TableSkeleton";
import { TableErrorState, EmptyState } from "@/components/Common/ErrorState";
import { toast } from "sonner";
import { Pagination } from "@/components/Common/Pagination";

function getMonitoringBadgeClassName(type: MonitoringType): string {
  const baseClasses =
    "flex h-5 w-5 items-center justify-center rounded-[6px] p-[3px] text-[14px]";
  const colorClass = MONITORING_BADGE_STYLES[type] || "bg-gray-500";

  return `${baseClasses} ${colorClass}`;
}

// Simple inline skeleton for pagination loading states
function InlineTableRowSkeleton() {
  return (
    <tr>
      <td className="px-4 py-1">
        <div className="h-4 w-full animate-pulse rounded bg-gray-300" />
      </td>
      <td className="px-4 py-1">
        <div className="space-y-1">
          <div className="h-4 w-24 animate-pulse rounded bg-gray-300" />
          <div className="h-3 w-32 animate-pulse rounded bg-gray-300" />
        </div>
      </td>
      <td className="px-4 py-1">
        <div className="flex items-center gap-2">
          <div className="h-5 w-5 animate-pulse rounded bg-gray-300" />
          <div className="h-4 w-24 animate-pulse rounded bg-gray-300" />
        </div>
      </td>
      <td className="px-4 py-1">
        <div className="h-4 w-full animate-pulse rounded bg-gray-300" />
      </td>
      <td className="px-4 py-1">
        <div className="h-4 w-12 animate-pulse rounded bg-gray-300" />
      </td>
      <td className="px-4 py-1">
        <div className="h-4 w-full animate-pulse rounded bg-gray-300" />
      </td>
      <td className="px-4 py-1">
        <div className="flex items-center gap-2">
          <div className="h-2 w-2 animate-pulse rounded-full bg-gray-300" />
          <div className="h-4 w-16 animate-pulse rounded bg-gray-300" />
        </div>
      </td>
    </tr>
  );
}

export default function TeamActivityLog() {
  const [displayPageSize, setDisplayPageSize] = useState(10);
  const [displayCurrentPage, setDisplayCurrentPage] = useState(1);

  const [cachePageSize] = useState(50); // Always fetch 50 items
  const [cacheCurrentPage, setCacheCurrentPage] = useState(1);
  const [isChangingPageSize, setIsChangingPageSize] = useState(false);
  const [cachedLogs, setCachedLogs] = useState<any[]>([]);
  const [lastPagination, setLastPagination] = useState<any>(null);

  const { data, isLoading, error, isFetching } = useGetTeamActivityLogs({
    page: cacheCurrentPage,
    limit: cachePageSize,
  });

  React.useEffect(() => {
    if (data?.logs) {
      if (cacheCurrentPage === 1) {
        setCachedLogs(data.logs);
      } else {
        setCachedLogs((prev) => [...prev, ...data.logs]);
      }
      setLastPagination(data.pagination);
    }
  }, [data, cacheCurrentPage]);

  const getDisplayData = () => {
    if (cachedLogs.length === 0) return { logs: [], pagination: null };

    const startIndex = (displayCurrentPage - 1) * displayPageSize;
    const endIndex = startIndex + displayPageSize;
    const displayLogs = cachedLogs.slice(startIndex, endIndex);

    const totalCachedItems = cachedLogs.length;
    const displayTotalPages = Math.ceil(totalCachedItems / displayPageSize);
    const hasNextInCache = displayCurrentPage < displayTotalPages;
    const hasPreviousInCache = displayCurrentPage > 1;

    const isNearCacheEnd = displayCurrentPage >= displayTotalPages - 1;
    const hasMoreOnServer = lastPagination?.has_next || false;

    return {
      logs: displayLogs,
      pagination: {
        current_page: displayCurrentPage,
        total_pages: hasMoreOnServer
          ? displayTotalPages + 1
          : displayTotalPages, // +1 if more on server
        page_size: displayPageSize,
        total_records: hasMoreOnServer
          ? lastPagination?.total_items || totalCachedItems
          : totalCachedItems,
        has_next: hasNextInCache || hasMoreOnServer,
        has_previous: hasPreviousInCache,
      },
      needsPrefetch: isNearCacheEnd && hasMoreOnServer,
    };
  };

  const displayData = getDisplayData();

  const handlePageSizeChange = (newPageSize: number) => {
    setDisplayPageSize(newPageSize);
    setDisplayCurrentPage(1);

    const needsMoreData =
      newPageSize > cachedLogs.length && lastPagination?.has_next;

    if (needsMoreData) {
      setIsChangingPageSize(true);
      setCachedLogs([]);
      setCacheCurrentPage(1);
    }
  };

  const handlePageChange = (newPage: number) => {
    const startIndex = (newPage - 1) * displayPageSize;
    const needsNewData = startIndex >= cachedLogs.length;

    if (needsNewData && lastPagination?.has_next) {
      setCacheCurrentPage((prev) => prev + 1);
    }

    setDisplayCurrentPage(newPage);
  };

  React.useEffect(() => {
    if (data && isChangingPageSize && !isFetching) {
      setIsChangingPageSize(false);
    }
  }, [data, isChangingPageSize, isFetching]);

  React.useEffect(() => {
    if (displayData.needsPrefetch && !isFetching) {
      setCacheCurrentPage((prev) => prev + 1);
    }
  }, [displayData.needsPrefetch, isFetching]);

  const exportToCSV = () => {
    if (cachedLogs.length === 0) return;

    const headers = [
      "Job ID",
      "User Name",
      "User Email",
      "Token",
      "Endpoint",
      "Method",
      "Timestamp",
      "Status",
      "Error Message",
    ];
    const csvContent = [
      headers.join(","),
      ...cachedLogs.map((activity) =>
        [
          `"${activity.job_id}"`,
          `"${activity.user_name}"`,
          `"${activity.user_email}"`,
          `"${activity.token_preview}"`,
          `"${activity.endpoint}"`,
          `"${activity.method}"`,
          `"${formatActivityTimestamp(activity.timestamp)}"`,
          `"${activity.status}"`,
          `"${activity.error_message || ""}"`,
        ].join(","),
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `team-activity-log-${new Date().toISOString().split("T")[0]}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success("Activity log exported to CSV successfully");
  };

  const exportToJSON = () => {
    if (cachedLogs.length === 0) return;

    const exportData = {
      exported_at: new Date().toISOString(),
      total_records: cachedLogs.length,
      data: cachedLogs.map((activity) => ({
        job_id: activity.job_id,
        user_name: activity.user_name,
        user_email: activity.user_email,
        token_preview: activity.token_preview,
        endpoint: activity.endpoint,
        method: activity.method,
        timestamp: activity.timestamp,
        formatted_timestamp: formatActivityTimestamp(activity.timestamp),
        status: activity.status,
        error_message: activity.error_message,
      })),
    };

    const jsonContent = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonContent], {
      type: "application/json;charset=utf-8;",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `team-activity-log-${new Date().toISOString().split("T")[0]}.json`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success("Activity log exported to JSON successfully");
  };

  if (isLoading) {
    return <ActivityLogTableSkeleton />;
  }

  if (error) {
    return (
      <TableErrorState
        message="Failed to load activity log. Please try again."
        onRetry={() => window.location.reload()}
      />
    );
  }

  if (!data || (cachedLogs.length === 0 && !isLoading && !isChangingPageSize)) {
    return (
      <div className="flex h-full min-h-0 w-full flex-col space-y-5 rounded-lg bg-white p-5">
        <div className="space-y-2">
          <div className="text-lg leading-5 font-bold -tracking-wide text-neutral-950">
            Activity Log
          </div>
          <div className="text-xs leading-3 text-neutral-500">
            Recent API activity across your team
          </div>
        </div>
        <EmptyState
          icon={
            <i className="ri-file-list-3-line mb-2 text-4xl text-neutral-500" />
          }
          title="No activity logs found"
          message="There are no activity logs to display at this time."
        />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-col space-y-5 rounded-lg bg-white p-5">
      {/* Header */}
      <div className="flex flex-row items-center justify-between">
        <div className="space-y-2">
          <div className="text-lg leading-5 font-bold -tracking-wide text-neutral-950">
            Activity Log
          </div>
          <div className="text-xs leading-3 text-neutral-500">
            Recent API activity across your team
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            className="text-muted hover:text-muted gap-2.5 p-1.5 text-xs leading-normal font-bold tracking-normal hover:bg-white/30"
            variant="minimal"
            icon
            onClick={exportToCSV}
            disabled={cachedLogs.length === 0}
          >
            <i className="ri-file-excel-2-line text-sm font-light"></i>
            Export as CSV
          </Button>
          <Button
            className="text-muted hover:text-muted gap-2.5 p-1.5 text-xs leading-normal font-bold tracking-normal hover:bg-white/30"
            variant="minimal"
            icon
            onClick={exportToJSON}
            disabled={cachedLogs.length === 0}
          >
            <i className="ri-braces-line text-sm font-light"></i>
            Export as JSON
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="scrollbar-thin overflow-x-auto">
        <table className="min-w-max table-auto border-collapse text-sm">
          <thead>
            <tr className="text-left text-xs font-medium tracking-wide text-neutral-500 uppercase">
              <th className="px-4 py-3">Job ID</th>
              <th className="px-8 py-3">User</th>
              <th className="px-4 py-3">Token</th>
              <th className="px-4 py-3">Endpoint</th>
              <th className="px-4 py-3">Method</th>
              <th className="px-4 py-3">Timestamp</th>
              <th className="px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {(isFetching || isChangingPageSize) && cachedLogs.length > 0
              ? // Show skeleton rows with the new page size count during loading
                [...Array(displayPageSize)].map((_, i) => (
                  <InlineTableRowSkeleton key={`skeleton-${i}`} />
                ))
              : displayData.logs.map((activity) => {
                  // Check if token is premium
                  const isPremium =
                    activity.token_preview
                      ?.toLowerCase()
                      ?.includes("premium") ?? false;

                  const monitoringType: MonitoringType =
                    getMonitoringTypeFromToken(activity.token_preview);
                  const monitoringIcon = createMonitoringIcon(
                    monitoringType,
                    "text-sm",
                    true,
                  );

                  // Ensure status colors are properly applied
                  const getStatusColor = (status: string): string => {
                    switch (status.toUpperCase()) {
                      case "SUCCESS":
                        return "#10b981"; // green-500
                      case "ERROR":
                        return "#EF4444"; // red-500
                      case "PENDING":
                        return "#F59E0B"; // amber-500
                      case "CANCELLED":
                        return "#6B7280"; // gray-500
                      default:
                        return "#6B7280"; // gray-500
                    }
                  };

                  const statusColor = getStatusColor(activity.status);

                  return (
                    <tr key={activity.job_id}>
                      <td className="px-4 py-1 text-xs leading-4 font-medium -tracking-wide text-neutral-950">
                        <div className="whitespace-nowrap">
                          {activity.job_id}
                        </div>
                      </td>
                      <td className="px-8 py-1">
                        <div className="space-y-1">
                          <div className="text-xs leading-4 font-medium -tracking-wide whitespace-nowrap text-neutral-950">
                            {activity.hash_key_name ? (
                              <div className="flex items-center gap-1.5">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <i className="ri-key-line text-md text-neutral-500"></i>
                                  </TooltipTrigger>
                                  <TooltipContent
                                    side="left"
                                    className="max-w-xs"
                                  >
                                    <p>Hashkey</p>
                                  </TooltipContent>
                                </Tooltip>
                                <div className="text-xs leading-4 font-medium -tracking-wide text-neutral-950">
                                  {activity.hash_key_name}
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1.5">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <i className="ri-admin-line text-md text-neutral-500"></i>
                                  </TooltipTrigger>
                                  <TooltipContent
                                    side="left"
                                    className="max-w-xs"
                                  >
                                    <p>Manager</p>
                                  </TooltipContent>
                                </Tooltip>

                                <div className="text-xs leading-4 font-medium -tracking-wide text-neutral-950">
                                  {activity.user_name}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-1">
                        <div className="flex items-center gap-2 whitespace-nowrap">
                          {isPremium ? (
                            <div className="flex h-5 w-5 items-center justify-center rounded-[6px] bg-yellow-500 p-[3px]">
                              <i className="ri-star-line text-xs text-white"></i>
                            </div>
                          ) : (
                            <div
                              className={getMonitoringBadgeClassName(
                                monitoringType,
                              )}
                            >
                              {monitoringIcon}
                            </div>
                          )}
                          <span className="text-xs leading-4 font-medium -tracking-wide text-neutral-950">
                            {activity.token_preview}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-1 text-xs leading-4 font-medium -tracking-wide text-neutral-950">
                        <div className="whitespace-nowrap">
                          {activity.endpoint}
                        </div>
                      </td>
                      <td className="px-4 py-1 text-xs leading-4 font-medium -tracking-wide text-neutral-950">
                        <div className="whitespace-nowrap">
                          {activity.method}
                        </div>
                      </td>
                      <td className="px-4 py-1 text-xs leading-4 font-medium -tracking-wide text-neutral-950">
                        <div className="whitespace-nowrap">
                          {formatActivityTimestamp(activity.timestamp)}
                        </div>
                      </td>
                      <td className="px-4 py-1">
                        {activity.error_message ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex cursor-help items-center gap-2 whitespace-nowrap">
                                <span
                                  className="h-2 w-2 flex-shrink-0 rounded-full"
                                  style={{ backgroundColor: statusColor }}
                                />
                                <span className="py-1 text-xs leading-4 font-medium -tracking-wide text-neutral-950">
                                  {activity.status}
                                </span>
                                <i className="ri-error-warning-line text-xs text-red-500"></i>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="left" className="max-w-xs">
                              <p className="text-xs">
                                {activity.error_message}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <div className="flex items-center gap-2 whitespace-nowrap">
                            <span
                              className="h-2 w-2 flex-shrink-0 rounded-full"
                              style={{ backgroundColor: statusColor }}
                            />
                            <span className="py-1 text-xs leading-4 font-medium -tracking-wide text-neutral-950">
                              {activity.status}
                            </span>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {displayData.pagination && (
        <Pagination
          currentPage={displayData.pagination.current_page}
          totalPages={displayData.pagination.total_pages}
          pageSize={displayData.pagination.page_size}
          totalRecords={displayData.pagination.total_records}
          hasNext={displayData.pagination.has_next}
          hasPrevious={displayData.pagination.has_previous}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          isLightMode={true}
        />
      )}
    </div>
  );
}
